import { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import EmptyGridContent from '../../components/EmptyGridContent/EmptyGridContent';
import NavigationButton from '../../components/NavigationButton/NavigationButton';
import NavigationPage from '../../components/NavigationPage/NavigationPage';
import Pill from '../../components/Pill/Pill';
import { useConfirmationModalContext } from '../../hooks/useConfirmationModalContext';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectUserList,
  selectUserLoading,
} from '../../redux/slices/userSlice';
import { fetchUserList } from '../../redux/thunks/userThunks';
import { TooltipAnchor } from '../../services/app.service';
import {
  abortPromiseOnUnmount,
  getCurrentUserAccess,
  isValidCode,
} from '../../services/base.service';
import { appRoutePaths } from '../../services/route.service';
import { canUserAccessRoute } from '../../services/security.service';
import { activateUser, deactivateUser } from '../../services/user.service';
import { ApiResponseModel } from '../../types/ApiResponseModel';
import { PillType } from '../../types/PillType';
import { IconType } from '../../types/propTypes/EmptyGridPropTypes';
import { UserListItem } from '../../types/UserListModel';
import StyledSingleDropdown from '../../components/StyledSingleDropdown/StyledSingleDropdown';
import { setStyledDropdownSingleValue } from '../../utilities/helperUtilities';
import MuiTextField from '@mui/material/TextField';
import ActionButton from '../../components/ActionButton/ActionButton';
import { UserStatus } from '../../types/UserStatus';
import { getSchoolManagementListItems } from '../../services/schoolManagement.service';
import { SchoolManagementListItemModel } from '../../types/SchoolManagementListItemModel';
import { isActive, isCurrentUser, isDistrictUser } from '../../utilities/userUtilities';
import './UserList.css';

const UserList = (): ReactElement => {
  const { setOptions, showConfirmation } = useConfirmationModalContext();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectUserLoading);
  const users: UserListItem[] = useAppSelector(selectUserList);

  const [filterName, setFilterName] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterSchool, setFilterSchool] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [schoolList, setSchoolList] = useState<SchoolManagementListItemModel[]>([]);
  const getSchoolOptions = (): { value: string; label: string; text: string }[] => {
    return schoolList.map((school) => ({
      value: school.schoolIdentifier,
      label: school.schoolName,
      text: school.schoolName,
    }));
  };
  useEffect(() => {
    getSchoolManagementListItems().then((resp) => {
      if (resp?.data) {
        setSchoolList(resp.data);
      }
    });
  }, []);
  const schoolOptions = getSchoolOptions();

  useEffect(() => {
    const promise = dispatch(fetchUserList());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  const handleEdit = (userId: string): void => {
    navigate(appRoutePaths.UserEdit(userId));
  };

  const handleActivate = async (userId: string): Promise<void> => {
    setOptions({
      title: 'Activate user for district?',
      content: (
        <div className="confirm-container">
          Are you sure you want to activate this user for the district &quot;
          {getCurrentUserAccess()?.currentDistrict?.name}&quot;?
        </div>
      ),
      confirmText: 'Yes, activate user',
      cancelText: 'No, cancel',
      onOk: async (): Promise<ApiResponseModel<unknown>> => {
        const response = await activateUser(userId);
        if (isValidCode(response.status)) {
          dispatch(fetchUserList());
        }
        return response;
      },
    });

    await showConfirmation();
  };

  const handleDeactivate = async (userId: string): Promise<void> => {
    setOptions({
      title: 'Deactivate user for district?',
      content: (
        <div className="confirm-container">
          Are you sure you want to deactivate the user for the district &quot;
          {getCurrentUserAccess()?.currentDistrict?.name}&quot;?
        </div>
      ),
      confirmText: 'Yes, deactivate user',
      cancelText: 'No, cancel',
      onOk: async (): Promise<ApiResponseModel<unknown>> => {
        const response = await deactivateUser(userId);
        if (isValidCode(response.status)) {
          dispatch(fetchUserList());
        }
        return response;
      },
    });

    await showConfirmation();
  };

  const filteredUsers = users.filter((user) => {
    const matchesName = filterName === ''
      || (user.name && user.name.toLowerCase().includes(filterName.toLowerCase()));
    const matchesEmail = filterEmail === ''
      || (user.email && user.email.toLowerCase().includes(filterEmail.toLowerCase()));
    const matchesSchool = filterSchool === '' || !isDistrictUser()
      || (
        Array.isArray(user.assignedSchools)
        && user.assignedSchools.includes(filterSchool)
      );
    const matchesStatus = filterStatus === ''
      || user.userStatus.toLowerCase() === filterStatus.toLowerCase();
    return matchesName && matchesEmail && matchesSchool && matchesStatus;
  });

  const handleResetFilters = (): void => {
    setFilterName('');
    setFilterEmail('');
    setFilterSchool('');
    setFilterStatus('');
  };

  const statusOptions: { value: string; label: string; text: string }[] = [
    { value: UserStatus.Active, label: 'Active', text: 'Active' },
    { value: UserStatus.InActive, label: 'Inactive', text: 'Inactive' },
  ];

  return (
    <NavigationPage
      heading="Administration Portal"
      pageClass="user-management--list"
      isLoading={isLoading}
      loadingDataId="user-management-loader"
      loadingText="Getting user list"
    >
      <div className="container">
        <div className="name-filter">
          <div className="field-label">Name</div>
          <MuiTextField
            id="nameFilter"
            type="text"
            name="nameFilter"
            label="Name"
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
          />
        </div>
        <div className="email-filter">
          <div className="field-label">Email</div>
          <MuiTextField
            id="emailFilter"
            type="text"
            name="emailFilter"
            label="Email"
            value={filterEmail}
            onChange={(e) => setFilterEmail(e.target.value)}
          />
        </div>
        {isDistrictUser() && (
          <div className="school-filter">
            <StyledSingleDropdown
              fieldName="schoolFilter"
              options={schoolOptions}
              value={setStyledDropdownSingleValue(schoolOptions, filterSchool)}
              onChange={async (selected) => setFilterSchool(selected?.value || '')}
              labelText="School"
              isClearable={true}
              showLabel={true}
            />
          </div>
        )}
        <div className="status-filter">
          <StyledSingleDropdown
            fieldName="statusFilter"
            options={statusOptions}
            value={setStyledDropdownSingleValue(statusOptions, filterStatus)}
            onChange={async (selected) => setFilterStatus(selected?.value || '')}
            labelText="Status"
            isClearable={true}
            showLabel={true}
          />
        </div>
        <div className="">
          <ActionButton
            onClick={handleResetFilters}
            classes="no-wrap-text apply-filter"
            tooltipText="Reset Filters">
            <>Reset Filters</>
          </ActionButton>
        </div>
      </div>
      <table cellSpacing={0} className="page-view-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.length > 0 &&
            filteredUsers.map((user) => (
              <tr key={`${user.userId}`} data-testid={user.userId}>
                <td>
                  <NavigationButton
                    route={appRoutePaths.UserDetail(user.userId)}
                    visibleOverride={canUserAccessRoute(
                      appRoutePaths.UserDetail()
                    )}
                    tooltipText="Click to go to User Detail View"
                  >
                    <>{user.name}</>
                  </NavigationButton>
                </td>
                <td className="users--list--email">{user.email}</td>
                <td>
                  <Pill
                    value={user.userStatus}
                    pillType={PillType.USER_STATUS}
                    status={user.userStatus}
                  />
                </td>
                <td>
                  <div className="action--column">
                    {isActive(user.userStatus) ? (
                      <>
                        <button
                          data-testid={`edit-button-${user.userId}`}
                          aria-label="Edit"
                          onClick={() => handleEdit(user.userId)}
                          data-tooltip-id={TooltipAnchor}
                          data-tooltip-content={'Edit User Access'}
                        >
                          <img src={'/images/icon-edit.svg'} />
                        </button>
                        {!isCurrentUser(user.userId) && (
                          <button
                            data-testid={`deactivate-button-${user.userId}`}
                            aria-label="Deactivate"
                            onClick={async () =>
                              await handleDeactivate(user.userId)
                            }
                            data-tooltip-id={TooltipAnchor}
                            data-tooltip-content={'Deactivate User'}
                          >
                            <img src={'/images/icon-unassign.svg'} />
                          </button>
                        )}
                      </>
                    ) : (
                      <button
                        data-testid={`activate-button-${user.userId}`}
                        aria-label="Activate"
                        onClick={async () => await handleActivate(user.userId)}
                        data-tooltip-id={TooltipAnchor}
                        data-tooltip-content={'Activate User'}
                      >
                        <img src={'/images/icon-activate.svg'} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
        </tbody>
      </table>
      {filteredUsers.length === 0 && (
        <EmptyGridContent iconType={IconType.FILE}>
          <p>No users found</p>
        </EmptyGridContent>
      )}
    </NavigationPage>
  );
};

export default UserList;
